import React from "react";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import FormatQuoteIcon from "@material-ui/icons/FormatQuote";
import ScheduleIcon from "@material-ui/icons/Schedule";
import SecurityIcon from "@material-ui/icons/Security";
import StyleIcon from "@material-ui/icons/Style";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import { APP_STRINGS } from "constants/appStrings";

const useStyles = makeStyles((theme) => ({
  section: {
    width: "min(1240px, calc(100% - 48px))",
    margin: "0 auto",
    padding: theme.spacing(6, 0, 2),
    [theme.breakpoints.down("sm")]: {
      width: "min(100% - 32px, 1240px)",
    },
  },
  wrapper: {
    padding: theme.spacing(4),
    borderRadius: 36,
    background:
      "linear-gradient(180deg, rgba(248,243,236,0.94) 0%, rgba(255,255,255,0.88) 100%)",
    border: "1px solid rgba(31, 38, 50, 0.08)",
    boxShadow: "0 30px 70px rgba(22, 29, 40, 0.08)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
    },
  },
  header: {
    maxWidth: 760,
  },
  eyebrow: {
    display: "inline-flex",
    padding: theme.spacing(1, 2),
    borderRadius: 999,
    background: "rgba(212, 179, 127, 0.14)",
    color: "#8d6d42",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
  },
  title: {
    margin: theme.spacing(2, 0),
    fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    fontSize: "clamp(2.5rem, 4vw, 3.9rem)",
    lineHeight: 0.96,
    color: "#5d3342",
  },
  subtitle: {
    color: "#5f6775",
    fontSize: 18,
    lineHeight: 1.9,
  },
  diffCard: {
    height: "100%",
    padding: theme.spacing(3),
    borderRadius: 28,
    background: "#fff",
    border: "1px solid rgba(31, 38, 50, 0.08)",
    boxShadow: "0 22px 50px rgba(22, 29, 40, 0.08)",
  },
  diffIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 54,
    height: 54,
    borderRadius: 18,
    background: "rgba(212, 179, 127, 0.16)",
    color: "#8c6a3e",
    marginBottom: theme.spacing(2),
  },
  diffTitle: {
    color: "#1e2430",
    fontWeight: 700,
    fontSize: 20,
    marginBottom: theme.spacing(1),
  },
  diffText: {
    color: "#5f6775",
    lineHeight: 1.8,
    fontSize: 15,
  },
  quoteCard: {
    position: "relative",
    height: "100%",
    padding: theme.spacing(4),
    borderRadius: 32,
    background: "linear-gradient(135deg, #6d3d4d 0%, #8c5366 100%)",
    color: "#f7f2ea",
    boxShadow: "0 32px 70px rgba(98, 54, 69, 0.18)",
  },
  quoteIcon: {
    color: "rgba(212, 179, 127, 0.72)",
    fontSize: 42,
    marginBottom: theme.spacing(2),
  },
  quoteText: {
    fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
    fontSize: 36,
    lineHeight: 1.05,
    color: "#fff6ea",
    marginBottom: theme.spacing(2),
  },
  quoteSubtext: {
    color: "rgba(247, 242, 234, 0.76)",
    lineHeight: 1.9,
    fontSize: 16,
    maxWidth: 520,
  },
  quoteBadge: {
    marginTop: theme.spacing(3),
    display: "inline-flex",
    padding: theme.spacing(1, 1.5),
    borderRadius: 999,
    background: "rgba(255,255,255,0.08)",
    color: "#f3dec0",
    fontSize: 12,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    fontWeight: 700,
  },
  cta: {
    marginTop: theme.spacing(4),
    minHeight: 54,
    padding: theme.spacing(0, 3),
    borderRadius: 999,
    background: "linear-gradient(135deg, #d1b07a 0%, #c39b63 100%)",
    color: "#5b3240",
    fontWeight: 700,
  },
}));

const cardIcons = [<SecurityIcon />, <ScheduleIcon />, <StyleIcon />];

export default function SectionPartners({ rollPageOnClickButton }) {
  const classes = useStyles();

  return (
    <div className={classes.section}>
      <div className={classes.wrapper}>
        <GridContainer spacing={4}>
          <GridItem xs={12} md={5}>
            <div className={classes.header}>
              <span className={classes.eyebrow}>{APP_STRINGS.partners.eyebrow}</span>
              <Typography component="h2" className={classes.title}>
                {APP_STRINGS.partners.title}
              </Typography>
              <Typography component="p" className={classes.subtitle}>
                {APP_STRINGS.partners.subtitle}
              </Typography>
            </div>
          </GridItem>

          <GridItem xs={12} md={7}>
            <GridContainer spacing={3}>
              {APP_STRINGS.partners.cards.map((item, index) => (
                <GridItem key={item.title} xs={12} sm={4}>
                  <div className={classes.diffCard}>
                    <div className={classes.diffIcon}>{cardIcons[index]}</div>
                    <Typography component="h3" className={classes.diffTitle}>
                      {item.title}
                    </Typography>
                    <Typography component="p" className={classes.diffText}>
                      {item.text}
                    </Typography>
                  </div>
                </GridItem>
              ))}
            </GridContainer>
          </GridItem>

          <GridItem xs={12}>
            <div className={classes.quoteCard}>
              <FormatQuoteIcon className={classes.quoteIcon} />
              <Typography component="div" className={classes.quoteText}>
                {APP_STRINGS.partners.quote}
              </Typography>
              <Typography component="p" className={classes.quoteSubtext}>
                {APP_STRINGS.partners.quoteText}
              </Typography>
              <Box className={classes.quoteBadge}>
                {APP_STRINGS.partners.quoteBadge}
              </Box>
              <Box>
                <Button
                  className={classes.cta}
                  onClick={() => rollPageOnClickButton("contact")}
                >
                  {APP_STRINGS.partners.cta}
                </Button>
              </Box>
            </div>
          </GridItem>
        </GridContainer>
      </div>
    </div>
  );
}
